import NotificationSounds, {
  playSampleSound,
  stopSampleSound,
} from 'react-native-notification-sounds';
import { AppStateSlice } from '../../stores/app_state';
import BackgroundTimer from '../BackgroundTimer';
import { TimerStore } from './types';

const default_sound_id = '22';

/** Contains global application timer info and functions for  */
export const createTimerStore: AppStateSlice<TimerStore> = (set, get) => ({
  timer: {
    minutes: 30,
    current_minute_value: 0,
    progress: 0,

    increment() {
      set((state) => {
        /** Increment by a smaller amount if working in small numbers */
        if (state.timer.minutes < 5) {
          state.timer.minutes += 1;
        } else {
          state.timer.minutes += 5;
        }
      });
    },

    decrement() {
      set((state) => {
        if (state.timer.minutes <= 5) {
          /** Don't go below 1 minute */
          state.timer.minutes = Math.max(state.timer.minutes - 1, 1);
        } else {
          state.timer.minutes -= 5;
        }
      });
    },

    start() {
      const timer_id = BackgroundTimer.setInterval(() => {
        get().timer.tick();
      }, 1000);

      set((state) => ({
        timer: {
          ...state.timer,
          start_time: new Date(),
          current_minute_value: state.timer.minutes,
          is_running: true,
          timer_id,
        },
      }));
    },

    pause() {
      const now_time = new Date().getTime();

      set((state) => {
        const { timer_id, resume_time } = state.timer;

        if (timer_id) {
          BackgroundTimer.clearInterval(timer_id);
          state.timer.timer_id = undefined;
        }

        if (resume_time) {
          state.timer.saved_elapsed_time =
            state.timer.saved_elapsed_time ||
            0 + (now_time - resume_time.getTime());
        } else if (state.timer.start_time) {
          state.timer.saved_elapsed_time =
            now_time - state.timer.start_time.getTime();
        }

        state.timer.resume_time = undefined;
        state.timer.paused = true;
      });
    },

    resume() {
      set((state) => {
        state.timer.resume_time = new Date();
        state.timer.paused = false;

        state.timer.timer_id = BackgroundTimer.setInterval(() => {
          get().timer.tick();
        }, 1000);
      });
    },

    stop() {
      set(({ timer }) => {
        timer.silence();

        if (timer.timer_id) {
          BackgroundTimer.clearInterval(timer.timer_id);
        }

        return {
          timer: {
            ...timer,
            is_running: false,
            start_time: undefined,
            resume_time: undefined,
            paused: false,
            progress: 0,
            current_minute_value: 0,
            done: false,
            saved_elapsed_time: undefined,
            timer_id: undefined,
            timeout_id: undefined,
          },
        };
      });
    },

    end() {
      /** Play the sound */
      NotificationSounds.getNotifications('alarm').then((sounds) => {
        const default_sound = sounds.find(
          (sound) => sound.soundID === default_sound_id
        );

        if (default_sound) {
          playSampleSound(default_sound);
        }
      });

      set((state) => {
        state.timer.timeout_id = BackgroundTimer.setTimeout(() => {
          get().timer.silence();
        }, 60000 * 2); // Stop sound after 2 minutes;

        /** Clear the running timer */
        if (state.timer.timer_id) {
          BackgroundTimer.clearInterval(state.timer.timer_id);
          state.timer.timer_id = undefined;
        }

        state.timer.done = true;
      });
    },

    silence() {
      stopSampleSound();

      set((state) => {
        if (state.timer.timeout_id) {
          BackgroundTimer.clearTimeout(state.timer.timeout_id);
          state.timer.timeout_id = undefined;
        }
      });
    },

    tick() {
      const { minutes, resume_time, saved_elapsed_time, start_time } =
        get().timer;

      const now_time = new Date().getTime();

      let elapsed = 0;
      const target_milliseconds = minutes * 60000;

      if (resume_time) {
        const resumed_time = resume_time.getTime();
        const already_elapsed_time = saved_elapsed_time || 0;

        elapsed = now_time - resumed_time + already_elapsed_time;
      } else if (start_time) {
        elapsed = now_time - start_time.getTime();
      }

      elapsed = Math.min(elapsed, target_milliseconds);

      const done = elapsed >= target_milliseconds;

      set(({ timer }) => ({
        timer: {
          ...timer,
          progress: (elapsed / target_milliseconds) * 100,
          current_minute_value: minutes - Math.floor(elapsed / 60000),
        },
      }));

      if (done) {
        get().timer.end();
      }
    },
  },
});
